import styled from "styled-components";
import { useIntl } from "react-intl";
import { useNavigate } from "react-router";
import { useTripPlannerContext } from "src/domain/TripPlanner/hooks/useTripPlannerContext";
import { isSaveableSearchResponse } from "src/domain/TripPlanner/util/isSaveableSearchResponse";
import { isRouteSaved } from "src/domain/TripPlanner/util/isRouteSaved";
import { tripHashFromSearchResponse } from "src/utils/location/createTripHashForCard";
import { TripNavbar } from "src/domain/TripPlanner/TripNavbar/TripNavbar";
import { getSchedulesPaintPerformanceKey } from "src/utils/performance";
import { sendAnalyticsInteractionEvent } from "src/analytics/sendAnalyticsEvent";
import { transitModeFromSegment } from "src/utils/adapters/transitMode";
import type { Mode } from "src/utils/types/mode";
import { spacing } from "src/design-system/tokens/spacing";
import { RouteHeader } from "../RouteHeader/RouteHeader";
import { Navbar } from "../../../components/Navbar/Navbar";
import messages from "../RouteScreen.messages";
import { useAnalyticsPageView } from "../../../utils/hooks/useAnalyticsPageView";
import { createRouteScreenViewModel } from "../createRouteScreenViewModel";
import type { SearchResponse } from "../../../api/SearchResponse";
import { hashChange } from "../../../utils/location/hashChange";
import { useLayout } from "../../../utils/hooks/useLayout";
import { useAnalyticsCoreExperience } from "../../../analytics/useAnalyticsCoreExperience/useAnalyticsCoreExperience";
import { TroniconRouteTimeline } from "../../../components/Timeline/UnscheduledTimeline/RouteTimeline/TroniconRouteTimeline";
import { getTransportModes } from "../../../analytics/getTransportModes/getTransportModes";
import { useTypedLocation } from "../../../utils/hooks/useTypedLocation";
import { navigateToNewStateHash } from "../../../utils/location/navigateToNewStateHash";
import { RouteScreenAds } from "../RouteScreenAds/RouteScreenAds";

type Props = {
  context: "transport" | "tripPlanner";
  searchResponse: SearchResponse;
  routeIndex: number;
};

export function RouteScreenLoaded(props: Props) {
  const intl = useIntl();
  const navigate = useNavigate();
  const location = useTypedLocation();
  const layout = useLayout();
  const { tripPlannerDetails } = useTripPlannerContext();
  const isSaved = isRouteSaved(
    tripPlannerDetails,
    props.searchResponse,
    props.routeIndex,
  );

  const routeCanonical =
    props.searchResponse.routes[props.routeIndex].canonicalName;

  const viewModel = createRouteScreenViewModel(
    props.searchResponse,
    props.routeIndex,
  );

  useAnalyticsPageView(
    {
      pagePath: location.pathname + location.search + location.hash,
      pageLocation: window.location.href,
    },
    props.searchResponse,
    "Route",
    getTransportModes(props.searchResponse, props.routeIndex),
    ["RouteSummary", "Timeline", "HotelCtaTimeline", "DisplayAd", "CsaAd"],
    undefined,
    viewModel.carriers,
  );

  useAnalyticsCoreExperience({
    experienceHash: `${location.pathname}:r${routeCanonical}`,
    analyticsLabel: "Route Screen",
  });

  function handleBackClick() {
    navigate(hashChange(undefined, location));
  }

  function handleDetailClick(segmentIndex?: number) {
    let mode: Mode | undefined = undefined;
    if (segmentIndex !== undefined) {
      performance.mark(
        getSchedulesPaintPerformanceKey(
          "NavigateStart",
          props.routeIndex,
          segmentIndex,
        ),
      );
      mode = transitModeFromSegment(
        props.searchResponse,
        props.routeIndex,
        segmentIndex,
      );
    }
    sendAnalyticsInteractionEvent({
      category: "RouteTimeline",
      action: "Click:Segment",
      label: mode,
    });
  }

  function handleTripBackClick() {
    navigateToNewStateHash(
      navigate,
      {
        highlightedTab: "trips",
      },
      tripHashFromSearchResponse(props.searchResponse),
      {
        ...location,
      },
    );
  }

  const breadcrumbProps = {
    showRouteBreadcrumb: true,
    showSearchBreadcrumb: true,
  };

  return (
    <RouteScreenWrapper>
      {props.context === "transport" && layout === "mobile" && (
        <Navbar
          headingText={intl.formatMessage(messages.heading)}
          onBackClick={handleBackClick}
        />
      )}
      <Content>
        {props.context === "tripPlanner" && (
          <TripNavbar
            isSaved={isSaved}
            onBackClick={handleTripBackClick}
            isSaveableScreen={isSaveableSearchResponse(props.searchResponse)}
            headingText={intl.formatMessage(messages.heading)}
            variant={layout === "desktop" ? "fullScreen" : "default"}
            {...breadcrumbProps}
          />
        )}
        <RouteHeader
          title={viewModel.title}
          priceRange={viewModel.priceRange}
          durationInMinutes={viewModel.durationInMinutes}
          routeCanonical={routeCanonical}
          onBackClick={
            props.context === "tripPlanner"
              ? handleTripBackClick
              : handleBackClick
          }
          hidePaneNav={props.context === "tripPlanner"}
        />
        <TroniconRouteTimeline
          context={props.context}
          routeIndex={props.routeIndex}
          onDetailClick={handleDetailClick}
        />
        {layout === "desktop" && props.context === "tripPlanner" && (
          <RouteScreenAds />
        )}
      </Content>
    </RouteScreenWrapper>
  );
}

const Content = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: ${spacing.xl};
`;

const RouteScreenWrapper = styled.div`
  display: inline;
`;
